/**
 * @file src/room/roomRoster.js
 * @description The room seam for a COLLECTION room boot — what the collection app hands the sync boot
 * so it can arm the room client. A module singleton with a set/update/get/clear seam, the same
 * no-window-globals pattern the room store and shared clock use.
 *
 * In the DECK model a room is built from a fixed number of DECKS (turntables), chosen at creation —
 * never from the collection's track count. So this seam carries two things:
 *
 *  - `deckCount`: the room shape (voice count). Fixed for the session; it is what a `create` mint
 *    sends as `shape.voices`, and it is what tells the sync boot "this collection boot is a room" even
 *    while every deck is still empty (decks start empty; players deal from the pool after Start).
 *  - `voiceOrder`: the LIVE positional stage→voice map — index i is wire slot i, the runtime voice id
 *    currently occupying deck i (a hole/`null` for an empty deck). It changes every time a card is
 *    loaded or cleared, so the collection app republishes it; the room store re-seeds its wire↔voice
 *    translation from it. Index alignment is load-bearing, so holes are preserved (never compacted).
 *  - `collection`: what the pool is CALLED and what it looks like (`{ name, image }`). Display-only
 *    and never on the wire — the waiting room shows it so the people gathering can see which
 *    collection they are about to play, instead of an opaque room id.
 *  - `joinCode`: the room's sayable code (`bee-orchid-4821`), or null for a room that has none. It
 *    reaches us with the GRANT, on the collection read — never off the boot URL, because the waiting
 *    room prints it as this room's code and a link can be handed to you with any code written on it.
 *  - `players`: how many people hold a SEAT. Same provenance as the code and for the same reason —
 *    it rides with the grant, not the link. The creator's tab sends it as `shape.players` when it
 *    mints, and the relay refuses a room without one, so this is the seat count's only source on a
 *    collection boot.
 *
 * An inline-roster multi boot doesn't use this: its order is already in the URL (`?roster=`).
 */

// { deckCount: number, players: (number|null), voiceOrder: Array<string|null>,
// collection: ({name,image}|null), joinCode: (string|null) } | null — null outside a collection
// room boot.
let roomRoster = null;

/** Coerce to a positional array, preserving index (an empty/hole deck stays `null` at its slot). */
function normalizeOrder(voiceOrder) {
  if (!Array.isArray(voiceOrder)) return [];
  return voiceOrder.map((id) => (typeof id === 'string' && id.length > 0 ? id : null));
}

/**
 * Arm the seam for a collection room boot with the fixed deck count (and an optional initial voice
 * order — empty in the deck model, since decks start empty). A deckCount < 1 clears the seam.
 */
export function setRoomRoster(spec) {
  const deckCount = spec?.deckCount;
  const count = Number.isInteger(deckCount) && deckCount > 0 ? deckCount : 0;
  roomRoster =
    count > 0
      ? {
          deckCount: count,
          players: Number.isInteger(spec?.players) && spec.players > 0 ? spec.players : null,
          voiceOrder: normalizeOrder(spec?.voiceOrder),
          collection: normalizeCollection(spec?.collection),
          joinCode: typeof spec?.joinCode === 'string' && spec.joinCode.trim() ? spec.joinCode.trim() : null,
          // Optional pre-resolved score runtime. The score entity/composer owns producing this;
          // this seam only carries the plain deadline + seat-grant data to room:create.
          ...(spec?.score && typeof spec.score === 'object' ? { score: spec.score } : {}),
          // Optional card DRAW — how many of each kind a section's hand holds — and the PINNED pool
          // it is dealt from. Both travel to room:create for the same reason the score does: a
          // link-joiner cannot ask the backend about a grant they only hold an id for, so the
          // creator states them once and the relay replays them to everybody.
          ...(spec?.draw && typeof spec.draw === 'object' ? { draw: spec.draw } : {}),
          ...(Array.isArray(spec?.pinned) && spec.pinned.length > 0 ? { pinned: spec.pinned } : {}),
        }
      : null;
}

/**
 * Display-only pool identity. Both fields are optional — a collection with no cover, or a payload
 * that omitted the name, still yields a usable object rather than forcing the waiting room to guess
 * whether `null` means "no collection" or "no name".
 */
function normalizeCollection(collection) {
  const name = typeof collection?.name === 'string' && collection.name.trim() ? collection.name.trim() : null;
  const image = typeof collection?.image === 'string' && collection.image.trim() ? collection.image.trim() : null;
  return name || image ? { name, image } : null;
}

/** Republish just the live positional stage→voice map (keeps the fixed deck count). No-op if unarmed. */
export function setRoomVoiceOrder(voiceOrder) {
  if (!roomRoster) return;
  roomRoster = { ...roomRoster, voiceOrder: normalizeOrder(voiceOrder) };
}

/** The armed room seam (`{ deckCount, players, voiceOrder, collection, joinCode, score, draw,
 *  pinned }`), or null outside a collection room boot. */
export function getRoomRoster() {
  return roomRoster;
}

/** Forget the seam (collection teardown / tests). */
export function clearRoomRoster() {
  roomRoster = null;
}
